import React from 'react';

import {
  ICtaDestination,
  IRegistrationEntrypointFields
} from '@/types/contentful';
import type { AffiliateHero2Props, greenlightBrandingEnum } from '../index';

import { getImageData } from '~/utils/contentful';

import CtaWidget from '../../../CtaWidget';
import { AffiliateLogoGrouping } from '../AffiliateLogoGrouping';
import PrivacyPolicyDisclaimer from '../../../PrivacyPolicyDisclaimer';

import { MobileImage, CTAText } from '../styles';
import cx from 'classnames';

interface PropsWithOverride extends AffiliateHero2Props {
  appsFlyerOverride?: string;
}

export default function MobileView({
  mobileHeadingText,
  mobileSubheadingText,
  mobileImage,
  mobileCta,
  affiliateLogo,
  useGreenlightIcon,
  greenlightBranding,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  sendConsentEvent,
  useEmbeddedSmartLink,
  hideLogoOnMobile,
  appsFlyerOverride,
  leftAlignOnMobile
}: PropsWithOverride) {
  return (
    <div className="block tablet:hidden">
      {!hideLogoOnMobile && (
        <AffiliateLogoGrouping
          affiliateLogo={affiliateLogo}
          useGreenlightIcon={useGreenlightIcon}
          greenlightBranding={greenlightBranding as greenlightBrandingEnum}
        />
      )}
      <div className="column w-full">
        <div className="relative flex-1">
          {mobileImage && (
            <MobileImage
              {...getImageData(mobileImage)}
              heroImage={true}
              loading="eager"
              data-testid="affiliate-hero-20-mobile-background"
            />
          )}
        </div>
      </div>

      <div className="block-container mt-8" data-e2e="hero">
        <div className="layout layout-padding">
          <div className="column w-11/12 tablet:w-8/12">
            <div
              className={cx(
                'mx-auto flex flex-col',
                leftAlignOnMobile
                  ? 'items-start justify-start'
                  : 'items-center justify-center'
              )}
              data-testid="affiliate-hero-20-hero-content-mobile"
            >
              <CTAText
                as="h1"
                className={cx(
                  'mx-auto pb-4 text-[40px] leading-[46px]',
                  leftAlignOnMobile ? 'text-left' : 'text-center'
                )}
                data-testid="affiliate-hero-20-hero-headline-mobile"
                fontSize="h1_0"
              >
                {(mobileHeadingText || '').replace(/<br>/g, ' ')}
              </CTAText>
              <p
                className={cx(
                  'mx-auto mt-4 pb-4 text-xl leading-[23px]',
                  leftAlignOnMobile ? 'text-left' : 'text-center'
                )}
                data-testid="affiliate-hero-20-hero-subheading-mobile"
              >
                {mobileSubheadingText}
              </p>

              {mobileCta && (
                <CtaWidget
                  {...mobileCta?.fields}
                  id="affiliate-hero-mobile-cta-widget"
                  sysId={mobileCta?.sys?.contentType?.sys?.id}
                  module="Hero"
                  sendConsentEvent={sendConsentEvent}
                  url={
                    useEmbeddedSmartLink && appsFlyerOverride
                      ? ({
                          fields: {
                            production: appsFlyerOverride,
                            staging: appsFlyerOverride,
                            development: appsFlyerOverride
                          }
                        } as ICtaDestination)
                      : (mobileCta?.fields as IRegistrationEntrypointFields)
                          ?.url
                  }
                />
              )}

              {privacyPolicyDisclaimer && (
                <div className="mt-6">
                  <PrivacyPolicyDisclaimer
                    privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                    displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
